import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { InsightService } from '../rum-service/insight.service';

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  error?: string;
  stack?: string;
  additionalData?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private readonly insightService = inject(InsightService);

  debug(
    context: string,
    message: string,
    additionalData?: Record<string, unknown>,
  ): void {
    this.log(LogLevel.DEBUG, context, message, undefined, additionalData);
  }

  info(
    context: string,
    message: string,
    additionalData?: Record<string, unknown>,
  ): void {
    this.log(LogLevel.INFO, context, message, undefined, additionalData);
  }

  warn(
    context: string,
    message: string,
    additionalData?: Record<string, unknown>,
  ): void {
    this.log(LogLevel.WARN, context, message, undefined, additionalData);
  }

  error(
    context: string,
    error: unknown,
    additionalData?: Record<string, unknown>,
  ): void {
    const normalizedError = this.normalizeError(error);
    this.log(
      LogLevel.ERROR,
      context,
      normalizedError.message,
      normalizedError,
      additionalData,
    );

    if (environment.awsrum?.enabled) {
      this.insightService.recordError(normalizedError);
    }
  }

  private log(
    level: LogLevel,
    context: string,
    message: string,
    error?: Error,
    additionalData?: Record<string, unknown>,
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context,
      message,
      ...(error
        ? {
            error: error.message,
            stack: error.stack,
          }
        : {}),
      ...(additionalData ? { additionalData } : {}),
    };

    this.writeToConsole(entry, error);
  }

  private writeToConsole(entry: LogEntry, error?: Error): void {
    const payload = {
      timestamp: entry.timestamp,
      context: entry.context,
      message: entry.message,
      ...(entry.additionalData ? { additionalData: entry.additionalData } : {}),
    };

    switch (entry.level) {
      case LogLevel.DEBUG:
        if (!environment.production) {
          console.debug(`[${entry.level}]`, payload);
        }
        return;
      case LogLevel.INFO:
        if (!environment.production) {
          console.info(`[${entry.level}]`, payload);
        }
        return;
      case LogLevel.WARN:
        console.warn(`[${entry.level}]`, payload);
        return;
      case LogLevel.ERROR:
        console.error(`[${entry.level}]`, payload, error ?? entry.error);
        return;
    }
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    return new Error(typeof error === 'string' ? error : JSON.stringify(error));
  }
}
